import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Checkbox,
    Collapse,
    Input,
  Modal,
  Progress,
  Radio,
  Segmented,
  Steps,
  Upload,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  FilePdfOutlined,
  GlobalOutlined,
  InboxOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import ChatWidgetPreview from "./ChatWidgetPreview";
import ProductLearnBanner from "./ProductLearnBanner";
import ChannelsSection from "./ChannelsSection";
import HubSpotConnectCard from "./HubSpotConnectCard";
import {
  BOT_STATUS,
  DEFAULT_BOT_DRAFT,
  LAUNCHER_POSITIONS,
  WIZARD_STEPS,
} from "./constants";
import { assistantPublished, getBotStatus, isOpaqueBotId } from "./channelKind";
import {
  buildProspectEmbedSnippet,
  createProspectBot,
  deleteKnowledgeDocument,
  deleteProspectBot,
  enableProspectEmbed,
  getKnowledgeSources,
  getProspectBot,
  setProspectBotStatus,
  startKnowledgeLinkCrawl,
  updateProspectBot,
  uploadKnowledgeDocument,
  prospectBotRagStatus,
} from "./prospectService";
import styles from "./style.module.scss";

const { TextArea } = Input;
const MAX_PDF_BYTES = 10 * 1024 * 1024;

function formatCrawlStats(site, t) {
  const pages = site.pages_unique ?? site.pages_crawled ?? 0;
  const chunks = site.chunks_indexed ?? 0;
  const chars = site.chars_indexed ?? 0;
  const words = chars > 0 ? Math.max(1, Math.round(chars / 5)) : 0;
  return t("sonic_prospect.crawl_stats", { pages, chunks, words });
}

function CrawlPagesAccordion({ site, t }) {
  const pages = Array.isArray(site.crawled_urls)
    ? site.crawled_urls.filter(Boolean)
    : [];
  const statsLabel = formatCrawlStats(site, t);
  if (!pages.length) {
    return <span className={styles.crawlMeta}>{statsLabel}</span>;
  }
  return (
    <Collapse
      ghost
      className={styles.crawlPagesCollapse}
      items={[
        {
          key: "pages",
          label: statsLabel,
          children: (
            <ul className={styles.crawlPageList}>
              {pages.map((url) => (
                <li key={url}>
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          ),
        },
      ]}
    />
  );
}

const BotWizard = ({
  bot: initialBot,
  onBack,
  onSaved,
  onDeleted,
  prospectLimits = null,
  whatsappConfig = null,
  embedded = false,
  initialStep = 0,
  focusName = 0,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("tr") ? "tr-TR" : "en-US";
  const [stepIndex, setStepIndex] = useState(initialStep);
  const [section, setSection] = useState(initialStep >= 3 ? "embed" : "general");
  const [bot, setBot] = useState({ ...DEFAULT_BOT_DRAFT, ...initialBot });
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [copied, setCopied] = useState(false);
  const [crawlUrl, setCrawlUrl] = useState("");
  const [crawlBusy, setCrawlBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState("");

  const retentionDays = prospectLimits?.vector_retention_days ?? 30;
  const sitePageLimit = prospectLimits?.max_pages || 200;
  const canDelete = Boolean(bot?.id);

  useEffect(() => {
    if (!focusName) return;
    setStepIndex(0);
    setSection("general");
  }, [focusName]);

  useEffect(() => {
    if (!focusName || stepIndex !== 0) return;
    const field = document.getElementById("sp-bot-name");
    if (field) field.focus();
  }, [focusName, stepIndex]);

  const handleDelete = async () => {
    if (!bot?.id) return;
    if (deleteTyped.trim() !== (bot.name || "").trim()) return;
    setDeleting(true);
    try {
      const result = await deleteProspectBot(bot.id);
      if (result?.vectorPurge?.scheduled) {
        message.success(
          t("sonic_prospect.delete_bot_success_with_purge", {
            days: result.vectorPurge.retention_days || retentionDays,
          }),
        );
      } else {
        message.success(t("sonic_prospect.delete_bot_success"));
      }
      setDeleteOpen(false);
      if (onDeleted) onDeleted();
      else onBack();
    } catch (_) {
      message.error(t("sonic_prospect.delete_bot_error"));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setBot({ ...DEFAULT_BOT_DRAFT, ...initialBot });
  }, [initialBot]);

  const embedSnippet = useMemo(() => buildProspectEmbedSnippet(bot), [bot]);
  const stepKey = WIZARD_STEPS[stepIndex];
  const siteUrl = String(bot.websiteUrl || "").trim();
  const defaultSystemPrompt = siteUrl
    ? t("sonic_prospect.default_system_prompt", {
        website: siteUrl,
        botName: bot.name || t("sonic_prospect.default_assistant_name"),
      })
    : t("sonic_prospect.default_system_prompt_no_site", {
        botName: bot.name || t("sonic_prospect.default_assistant_name"),
      });
  const published = assistantPublished(bot);
  const botLive = getBotStatus(bot) === "live";
  const publishLabel = botLive
    ? t("sonic_prospect.btn_publish_changes")
    : t("sonic_prospect.btn_publish");
  const systemPromptValue = bot.knowledge?.systemPrompt || defaultSystemPrompt;
  const usingDefaultPrompt = !(bot.knowledge?.systemPrompt || "").trim();
  const websites = bot.knowledge?.websites || [];
  const crawlInFlight = websites.some((site) => site.status === "processing");
  const ragInFlight = (() => {
    const status = prospectBotRagStatus(bot);
    return status === "pending" || status === "processing";
  })();

  useEffect(() => {
    if (stepKey !== "knowledge" || !bot.id || !crawlInFlight) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await getKnowledgeSources(bot.id);
        if (cancelled) return;
        setBot((prev) => ({
          ...prev,
          knowledge: { ...prev.knowledge, websites: data.websites },
        }));
      } catch (_) {
        /* keep last known status */
      }
    };
    tick();
    const timer = setInterval(tick, 10000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [stepKey, bot.id, crawlInFlight]);

  useEffect(() => {
    if (stepKey !== "knowledge" || !bot.id || !ragInFlight) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await getProspectBot(bot.id);
        if (cancelled || !fresh) return;
        setBot((prev) => ({
          ...prev,
          knowledge: { ...prev.knowledge, ...fresh.knowledge },
          knowledgeStats: fresh.knowledgeStats || prev.knowledgeStats,
        }));
      } catch (_) {
        /* keep polling */
      }
    };
    tick();
    const timer = setInterval(tick, 8000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [stepKey, bot.id, ragInFlight, bot.knowledge?.ragStatus]);

  const patchBot = (patch) => setBot((prev) => ({ ...prev, ...patch }));

  const persistedUrl = useRef(String(bot.websiteUrl || "").trim());

  const persistBot = useCallback(async () => {
    const nextUrl = String(bot.websiteUrl || "").trim();
    const prevUrl = persistedUrl.current;
    if (prevUrl && nextUrl && prevUrl !== nextUrl) {
      const accepted = await new Promise((resolve) => {
        Modal.confirm({
          title: t("sonic_prospect.website_change_title"),
          content: t("sonic_prospect.website_change_body"),
          okText: t("sonic_prospect.website_change_ok"),
          cancelText: t("sonic_prospect.confirm_cancel"),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!accepted) return null;
    }
    setSaving(true);
    try {
      const payload = { ...bot };
      let saved;
      if (bot.id) {
        saved = await updateProspectBot(bot.id, payload);
      } else {
        saved = await createProspectBot(payload);
      }
      setBot(saved);
      persistedUrl.current = String(saved?.websiteUrl || "").trim();
      if (onSaved) onSaved(saved);
      return saved;
    } catch (e) {
      message.error(t("sonic_prospect.msg_save_error"));
      throw e;
    } finally {
      setSaving(false);
    }
  }, [bot, onSaved, t]);

  const saveSection = async () => {
    try {
      const saved = await persistBot();
      if (saved) message.success(t("sonic_prospect.msg_saved"));
    } catch (_) {
      /* persistBot handles the error toast */
    }
  };

  const opaqueTried = useRef(null);
  useEffect(() => {
    if (!bot?.id || !bot?.slug || isOpaqueBotId(bot.slug)) return undefined;
    if (opaqueTried.current === bot.id) return undefined;
    opaqueTried.current = bot.id;
    let cancelled = false;
    enableProspectEmbed(bot.id, bot)
      .then((saved) => {
        if (!cancelled && saved) {
          setBot(saved);
          if (onSaved) onSaved(saved);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [bot, onSaved]);

  const goNext = async () => {
    if (!(bot.name || "").trim()) {
      message.warning(t("sonic_prospect.validation_name"));
      return;
    }
    try {
      let saved = await persistBot();
      if (!saved) return undefined;
      if (WIZARD_STEPS[stepIndex + 1] === "embed" && saved?.id) {
        try {
          saved = await enableProspectEmbed(saved.id, saved);
          setBot(saved);
          if (onSaved) onSaved(saved);
        } catch (_) {
          /* Go live can still provision the slug */
        }
      }
      if (stepIndex < WIZARD_STEPS.length - 1) {
        setStepIndex((i) => i + 1);
      }
      return saved;
    } catch (_) {
      /* persistBot handles toast */
    }
  };

  const handleEnableEmbed = async () => {
    if (!bot.id) {
      await goNext();
      return;
    }
    setSaving(true);
    try {
      let saved = await enableProspectEmbed(bot.id, bot);
      saved = await setProspectBotStatus(saved.id, BOT_STATUS.ACTIVE);
      setBot(saved);
      if (onSaved) onSaved(saved);
      message.success(t("sonic_prospect.msg_published"));
    } catch (_) {
      message.error(t("sonic_prospect.msg_publish_error"));
    } finally {
      setSaving(false);
    }
  };

  const copySnippet = async () => {
    if (!embedSnippet) {
      message.warning(t("sonic_prospect.validation_embed"));
      return;
    }
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopied(true);
      message.success(t("sonic_prospect.copied_toast"));
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      message.error(t("sonic_prospect.msg_copy_error"));
    }
  };

  const updateFaq = (index, field, value) => {
    const faqItems = [...(bot.knowledge?.faqItems || [])];
    faqItems[index] = { ...faqItems[index], [field]: value };
    patchBot({ knowledge: { ...bot.knowledge, faqItems } });
  };

  const addFaq = () => {
    const faqItems = [...(bot.knowledge?.faqItems || []), { q: "", a: "" }];
    patchBot({ knowledge: { ...bot.knowledge, faqItems } });
  };

  const removeFaq = (index) => {
    const faqItems = (bot.knowledge?.faqItems || []).filter((_, i) => i !== index);
    patchBot({ knowledge: { ...bot.knowledge, faqItems } });
  };

  const updateSystemPrompt = (value) => {
    patchBot({
      knowledge: {
        ...bot.knowledge,
        systemPrompt: value === defaultSystemPrompt ? "" : value,
      },
    });
  };

  const resetSystemPrompt = () => {
    patchBot({
      knowledge: { ...bot.knowledge, systemPrompt: "" },
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handlePdfUpload = async (file) => {
    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      message.error(t("sonic_prospect.pdf_invalid_type"));
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      message.error(t("sonic_prospect.pdf_too_large"));
      return;
    }
    const nameKey = (file.name || "").trim().toLowerCase();
    const already = (bot.knowledge?.pdfDocuments || []).some(
      (doc) => (doc.name || "").trim().toLowerCase() === nameKey,
    );
    if (already) {
      message.warning(t("sonic_prospect.pdf_duplicate"));
      return;
    }

    setUploadingPdf(true);
    try {
      let botId = bot.id;
      if (!botId) {
        const saved = await persistBot();
        botId = saved?.id;
      }
      if (!botId) {
        message.error(t("sonic_prospect.pdf_save_first"));
        return;
      }
      const { bot: updatedBot } = await uploadKnowledgeDocument(botId, file);
      setBot(updatedBot);
      if (onSaved) onSaved(updatedBot);
      message.success(t("sonic_prospect.pdf_uploaded"));
    } catch (e) {
      if (e?.message === "DUPLICATE_DOCUMENT") {
        message.warning(t("sonic_prospect.pdf_duplicate"));
        return;
      }
      message.error(t("sonic_prospect.pdf_upload_error"));
    } finally {
      setUploadingPdf(false);
    }
  };

  const handlePdfRemove = async (docId) => {
    if (!bot.id) return;
    setUploadingPdf(true);
    try {
      const updatedBot = await deleteKnowledgeDocument(bot.id, docId);
      setBot(updatedBot);
      if (onSaved) onSaved(updatedBot);
      message.success(t("sonic_prospect.pdf_removed"));
    } catch (_) {
      message.error(t("sonic_prospect.pdf_remove_error"));
    } finally {
      setUploadingPdf(false);
    }
  };

  const crawlErrorText = (code) => {
    if (code === "invalid_url" || code === "MISSING_PARAMETERS") {
      return t("sonic_prospect.crawl_error_invalid_url");
    }
    if (code === "no_pages") return t("sonic_prospect.crawl_error_no_pages");
    if (code === "DUPLICATE_DOCUMENT") return t("sonic_prospect.crawl_error_busy");
    return t("sonic_prospect.crawl_error_generic");
  };

  const handleStartCrawl = async (rawUrl) => {
    const value = (rawUrl || crawlUrl || "").trim();
    if (!value) {
      message.error(t("sonic_prospect.crawl_error_invalid_url"));
      return;
    }
    setCrawlBusy(true);
    try {
      let botId = bot.id;
      if (!botId) {
        const saved = await persistBot();
        botId = saved?.id;
      }
      if (!botId) {
        message.error(t("sonic_prospect.pdf_save_first"));
        return;
      }
      const res = await startKnowledgeLinkCrawl(botId, value, bot.language);
      setBot((prev) => {
        const incoming = res.website;
        const rest = (prev.knowledge?.websites || []).filter(
          (site) =>
            site.id !== incoming.id &&
            (site.url || "") !== (incoming.url || ""),
        );
        return {
          ...prev,
          knowledge: { ...prev.knowledge, websites: [incoming, ...rest] },
        };
      });
      if (!rawUrl) setCrawlUrl("");
      message.success(t("sonic_prospect.crawl_started"));
    } catch (e) {
      message.error(crawlErrorText(e?.message || ""));
    } finally {
      setCrawlBusy(false);
    }
  };

  const renderSetup = () => (
    <div className={styles.wizardGrid}>
      <div className={styles.wizardForm}>
        <label className={styles.fieldLabel}>{t("sonic_prospect.field_name")}</label>
        <Input
          id="sp-bot-name"
          value={bot.name}
          onChange={(e) => patchBot({ name: e.target.value })}
          placeholder={t("sonic_prospect.field_name_ph")}
        />
        <label className={styles.fieldLabel}>{t("sonic_prospect.field_website")}</label>
        <Input
          value={bot.websiteUrl}
          onChange={(e) => patchBot({ websiteUrl: e.target.value })}
          placeholder="https://www.siteniz.com"
        />
        <label className={styles.fieldLabel}>{t("sonic_prospect.field_language")}</label>
        <Segmented
          className={styles.langSegment}
          value={bot.language || "tr"}
          onChange={(value) => patchBot({ language: value })}
          options={[
            { label: "Türkçe", value: "tr" },
            { label: "English", value: "en" },
          ]}
        />
        <label className={styles.fieldLabel}>{t("sonic_prospect.field_greeting")}</label>
        <TextArea
          rows={3}
          value={bot.greeting}
          onChange={(e) => patchBot({ greeting: e.target.value })}
          placeholder={t("sonic_prospect.field_greeting_ph")}
        />
        <label className={styles.fieldLabel}>{t("sonic_prospect.field_goals")}</label>
        <div className={styles.goalChecks}>
          <Checkbox
            checked={bot.goals?.product_qa !== false}
            onChange={(e) =>
              patchBot({ goals: { ...bot.goals, product_qa: e.target.checked } })
            }
          >
            {t("sonic_prospect.goal_qa")}
          </Checkbox>
          <Checkbox
            checked={bot.goals?.lead_capture !== false}
            onChange={(e) =>
              patchBot({ goals: { ...bot.goals, lead_capture: e.target.checked } })
            }
          >
            {t("sonic_prospect.goal_lead")}
          </Checkbox>
          <Checkbox
            checked={!!bot.goals?.handoff}
            onChange={(e) =>
              patchBot({ goals: { ...bot.goals, handoff: e.target.checked } })
            }
          >
            {t("sonic_prospect.goal_handoff")}
          </Checkbox>
        </div>
        <Button type="primary" className={styles.primaryBtn} loading={saving} onClick={goNext}>
          {t("sonic_prospect.btn_continue_knowledge")}
        </Button>
      </div>
      <ChatWidgetPreview bot={bot} />
    </div>
  );

  const renderKnowledge = () => (
    <div className={styles.wizardGrid}>
      <div className={styles.wizardForm}>
        <p className={styles.stepIntro}>{t("sonic_prospect.knowledge_intro")}</p>

        <label className={styles.fieldLabel}>{t("sonic_prospect.field_system_prompt")}</label>
        <p className={styles.pdfHint}>{t("sonic_prospect.field_system_prompt_hint")}</p>
        <TextArea
          rows={5}
          value={systemPromptValue}
          onChange={(e) => updateSystemPrompt(e.target.value)}
          placeholder={defaultSystemPrompt}
        />
        <div className={styles.promptMeta}>
          {usingDefaultPrompt ? (
            <span className={styles.promptDefaultTag}>{t("sonic_prospect.prompt_using_default")}</span>
          ) : (
            <Button type="link" size="small" className={styles.promptReset} onClick={resetSystemPrompt}>
              {t("sonic_prospect.btn_reset_prompt")}
            </Button>
          )}
        </div>

        <label className={styles.fieldLabel}>{t("sonic_prospect.pdf_section_title")}</label>
        <p className={styles.pdfHint}>{t("sonic_prospect.pdf_section_hint")}</p>
        <Upload.Dragger
          className={styles.pdfUpload}
          accept=".pdf,application/pdf"
          showUploadList={false}
          multiple={false}
          disabled={uploadingPdf || saving}
          beforeUpload={(file) => {
            handlePdfUpload(file);
            return false;
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t("sonic_prospect.pdf_drop_title")}</p>
          <p className="ant-upload-hint">{t("sonic_prospect.pdf_drop_hint")}</p>
        </Upload.Dragger>
        {(bot.knowledge?.pdfDocuments || []).map((doc) => (
          <div key={doc.id} className={styles.pdfRow}>
            <FilePdfOutlined className={styles.pdfIcon} />
            <div className={styles.pdfMeta}>
              <span className={styles.pdfName}>{doc.name}</span>
              <span className={styles.pdfSize}>{formatFileSize(doc.size)}</span>
            </div>
            <Button
              type="text"
              danger
              icon={<MinusCircleOutlined />}
              disabled={uploadingPdf}
              onClick={() => handlePdfRemove(doc.id)}
            />
          </div>
        ))}

        <label className={styles.fieldLabel}>{t("sonic_prospect.crawl_section_title")}</label>
        <p className={styles.pdfHint}>
          {t("sonic_prospect.crawl_section_hint", {
            maxPages: sitePageLimit,
          })}
        </p>
        <div className={styles.crawlComposer}>
          <Input
            value={siteUrl}
            readOnly
            placeholder="https://www.siteniz.com"
          />
          <button
            type="button"
            className={styles.textLink}
            onClick={() => {
              if (published) setSection("general");
              else setStepIndex(0);
            }}
          >
            {t("sonic_prospect.url_change")}
          </button>
          <Button
            type="primary"
            loading={crawlBusy}
            disabled={crawlInFlight || !siteUrl}
            onClick={() => handleStartCrawl(siteUrl)}
          >
            {t("sonic_prospect.crawl_btn")}
          </Button>
        </div>
        <div className={styles.coverageBlock}>
          <div className={styles.coverageLabel}>
            <span>{t("sonic_prospect.scan_coverage")}</span>
            <span>
              {t("sonic_prospect.scan_coverage_value", {
                pages: websites.reduce(
                  (sum, site) =>
                    sum + (Number(site.pages_unique ?? site.pages_crawled) || 0),
                  0,
                ),
                max: sitePageLimit,
              })}
            </span>
          </div>
          <div className={styles.coverageTrack} aria-hidden="true">
            <span
              style={{
                width: `${Math.min(
                  100,
                  Math.round(
                    ((websites.reduce(
                      (sum, site) =>
                        sum + (Number(site.pages_unique ?? site.pages_crawled) || 0),
                      0,
                    ) || 0) /
                      sitePageLimit) *
                      100,
                  ),
                )}%`,
              }}
            />
          </div>
          {bot.knowledge?.ragUpdatedAt ? (
            <p className={styles.unscanned}>
              {t("sonic_prospect.scan_last", {
                time: new Date(bot.knowledge.ragUpdatedAt).toLocaleString("tr-TR"),
              })}
            </p>
          ) : null}
        </div>
        {websites.map((site) => {
          const pct = Number(site.progress_pct) || 0;
          const maxPages = site.max_pages || 0;
          return (
            <div key={site.id || site.url} className={styles.crawlCard}>
              <div className={styles.crawlCardHead}>
                <GlobalOutlined className={styles.crawlIcon} />
                <span className={styles.pdfName}>{site.url}</span>
                {site.smart_scan !== false ? (
                  <span className={styles.smartScanBadge}>
                    {t("sonic_prospect.crawl_smart_scan_badge")}
                  </span>
                ) : null}
                {site.status === "processing" ? <LoadingOutlined /> : null}
                {site.status === "completed" ? (
                  <CheckCircleOutlined className={styles.crawlOk} />
                ) : null}
                {site.status === "failed" ? (
                  <CloseCircleOutlined className={styles.crawlFail} />
                ) : null}
              </div>
              {site.status === "processing" ? (
                <>
                  <Progress percent={pct} size="small" />
                  <span className={styles.crawlMeta}>
                    {t("sonic_prospect.crawl_progress", {
                      crawled: site.pages_crawled || 0,
                      max: maxPages || "—",
                      pct,
                    })}
                  </span>
                </>
              ) : null}
              {site.status === "completed" ? (
                <CrawlPagesAccordion site={site} t={t} />
              ) : null}
              {site.status === "failed" ? (
                <span className={styles.crawlFailText}>
                  {crawlErrorText(site.error || "")}
                </span>
              ) : null}
              {site.status === "completed" || site.status === "failed" ? (
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  disabled={crawlBusy || crawlInFlight}
                  onClick={() => handleStartCrawl(site.url)}
                >
                  {t("sonic_prospect.crawl_again")}
                </Button>
              ) : null}
            </div>
          );
        })}

        <label className={styles.fieldLabel}>{t("sonic_prospect.faq_section_title")}</label>
        <p className={styles.pdfHint}>{t("sonic_prospect.faq_section_hint")}</p>
        {(bot.knowledge?.faqItems || []).map((item, index) => (
          <div key={index} className={styles.faqRow}>
            <Input
              value={item.q}
              onChange={(e) => updateFaq(index, "q", e.target.value)}
              placeholder={t("sonic_prospect.faq_question_ph")}
            />
            <TextArea
              rows={2}
              value={item.a}
              onChange={(e) => updateFaq(index, "a", e.target.value)}
              placeholder={t("sonic_prospect.faq_answer_ph")}
            />
            <Button
              type="text"
              danger
              icon={<MinusCircleOutlined />}
              onClick={() => removeFaq(index)}
            />
          </div>
        ))}
        <Button type="dashed" icon={<PlusOutlined />} onClick={addFaq} block>
          {t("sonic_prospect.btn_add_faq")}
        </Button>

        <div className={styles.wizardNav}>
          <Button onClick={() => setStepIndex(0)}>{t("sonic_prospect.btn_back")}</Button>
          <Button type="primary" loading={saving} onClick={goNext}>
            {t("sonic_prospect.btn_continue_appearance")}
          </Button>
        </div>
      </div>
      <ChatWidgetPreview bot={bot} />
    </div>
  );

  const renderAppearance = () => (
    <div className={styles.wizardGrid}>
      <div className={styles.wizardForm}>
        <label className={styles.fieldLabel}>{t("sonic_prospect.field_brand_color")}</label>
        <div className={styles.colorRow}>
          <input
            type="color"
            value={bot.appearance?.brandColor || "#7c3aed"}
            onChange={(e) =>
              patchBot({
                appearance: { ...bot.appearance, brandColor: e.target.value },
              })
            }
          />
          <Input
            value={bot.appearance?.brandColor || "#7c3aed"}
            onChange={(e) =>
              patchBot({
                appearance: { ...bot.appearance, brandColor: e.target.value },
              })
            }
          />
        </div>
        <label className={styles.fieldLabel}>{t("sonic_prospect.field_launcher")}</label>
        <Radio.Group
          value={bot.appearance?.launcherPosition || "bottom-right"}
          onChange={(e) =>
            patchBot({
              appearance: { ...bot.appearance, launcherPosition: e.target.value },
            })
          }
        >
          {LAUNCHER_POSITIONS.map((opt) => (
            <Radio key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </Radio>
          ))}
        </Radio.Group>
        <div className={styles.watermarkOption}>
          <Checkbox
            checked={!!bot.appearance?.hideWatermark}
            onChange={(e) =>
              patchBot({
                appearance: {
                  ...bot.appearance,
                  hideWatermark: e.target.checked,
                },
              })
            }
          >
            {t("sonic_prospect.field_remove_watermark")}
          </Checkbox>
          <p className={styles.pdfHint}>{t("sonic_prospect.field_remove_watermark_hint")}</p>
        </div>
        <div className={styles.wizardNav}>
          <Button onClick={() => setStepIndex(1)}>{t("sonic_prospect.btn_back")}</Button>
          <Button type="primary" loading={saving} onClick={goNext}>
            {t("sonic_prospect.btn_continue_embed")}
          </Button>
        </div>
      </div>
      <ChatWidgetPreview bot={bot} />
    </div>
  );

  const renderEmbed = () => (
    <div className={styles.wizardGrid}>
      <div className={styles.wizardForm}>
        <p className={styles.stepIntro}>
          {t("sonic_prospect.embed_intro", { action: publishLabel })}
        </p>
        {embedSnippet ? (
          <div className={styles.snippetWrap}>
            <button type="button" className={styles.snippetCopy} onClick={copySnippet} aria-label={t("sonic_prospect.btn_copy_embed")}>
              <CopyOutlined />
            </button>
            <pre className={styles.snippetBox}>{embedSnippet}</pre>
          </div>
        ) : (
          <p className={styles.embedHint}>{t("sonic_prospect.embed_hint")}</p>
        )}
        <div className={styles.embedActions}>
          <Button ghost icon={<CopyOutlined />} onClick={copySnippet} disabled={!embedSnippet}>
            {t("sonic_prospect.btn_copy_embed")}
          </Button>
          <Button type="primary" loading={saving} onClick={handleEnableEmbed}>
            {publishLabel}
          </Button>
        </div>
        <div className={styles.installCard}>
          <p className={styles.installTitle}>{t("sonic_prospect.install_title")}</p>
          <ol className={styles.installSteps}>
            <li>{t("sonic_prospect.install_step_1")}</li>
            <li>{t("sonic_prospect.install_step_2")}</li>
            <li>{t("sonic_prospect.install_step_3")}</li>
          </ol>
          <pre className={styles.installHtml}>
            <span>{"<footer>…</footer>"}</span>
            {"\n"}
            <span className={styles.installComment}>
              {`<!-- ${t("sonic_prospect.install_html_comment")} -->`}
            </span>
            {"\n"}
            <span className={styles.installPasteLine}>{"</body>"}</span>
            {"\n"}
            <span>{"</html>"}</span>
          </pre>
          <p className={styles.installHint}>{t("sonic_prospect.install_cms_hint")}</p>
        </div>
        <div className={styles.wizardNav}>
          <Button onClick={() => setStepIndex(2)}>{t("sonic_prospect.btn_back")}</Button>
        </div>
      </div>
      <ChatWidgetPreview bot={bot} />
    </div>
  );

  const stepContent = {
    setup: renderSetup(),
    knowledge: renderKnowledge(),
    appearance: renderAppearance(),
    embed: renderEmbed(),
  };

  return (
    <div className={`${styles.wizardPage}${embedded ? ` ${styles.embeddedPane}` : ""}`}>
      {embedded ? null : (
      <button type="button" className={styles.backLink} onClick={onBack}>
        <ArrowLeftOutlined /> {t("sonic_prospect.btn_back_list")}
      </button>
      )}
      {embedded ? null : (
      <header className={styles.pageHeader}>
        <div>
          <h1>{t("sonic_prospect.page_title")}</h1>
          <p>{t("sonic_prospect.page_subtitle")}</p>
        </div>
      </header>
      )}
      {embedded ? null : <ProductLearnBanner />}
      {published ? (
        <>
          <div className={styles.settingsNav} role="tablist">
            {[
              ["general", "sonic_prospect.nav_general"],
              ["knowledge", "sonic_prospect.nav_knowledge"],
              ["appearance", "sonic_prospect.nav_appearance"],
              ["channels", "sonic_prospect.nav_channels"],
              ["crm", "sonic_prospect.nav_crm"],
              ["embed", "sonic_prospect.nav_embed"],
              ["danger", "sonic_prospect.nav_danger"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                className={section === id ? styles.detailTabOn : styles.detailTab}
                onClick={() => setSection(id)}
              >
                {t(label)}
              </button>
            ))}
          </div>
          <div className={`${styles.stepBody} ${styles.publishedPane}`}>
            {section === "general" ? renderSetup() : null}
            {section === "knowledge" ? renderKnowledge() : null}
            {section === "appearance" ? renderAppearance() : null}
            {section === "channels" ? (
              <ChannelsSection
                bot={bot}
                whatsappConfig={whatsappConfig}
                onOpenEmbed={() => setSection("embed")}
                onUpdated={(saved) => {
                  setBot(saved);
                  if (onSaved) onSaved(saved);
                }}
              />
            ) : null}
            {section === "crm" ? <HubSpotConnectCard /> : null}
            {section === "embed" ? renderEmbed() : null}
            {section === "danger" && canDelete ? (
              <section className={styles.dangerZone}>
                <h2>{t("sonic_prospect.danger_title")}</h2>
                <Button danger type="text" onClick={() => { setDeleteTyped(""); setDeleteOpen(true); }}>
                  {t("sonic_prospect.delete_bot_btn")}
                </Button>
              </section>
            ) : null}
            {section !== "danger" && section !== "channels" && section !== "crm" && section !== "embed" ? (
              <Button type="primary" loading={saving} onClick={saveSection}>
                {t("sonic_prospect.btn_save")}
              </Button>
            ) : null}
            {section === "embed" ? (
              <Button loading={saving} onClick={saveSection}>
                {t("sonic_prospect.btn_save")}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <Steps
            current={stepIndex}
            className={styles.stepper}
            items={WIZARD_STEPS.map((key) => ({
              title: t(`sonic_prospect.step_${key}`),
            }))}
          />
          <div className={styles.stepBody}>{stepContent[stepKey]}</div>
          <ChannelsSection
            bot={bot}
            whatsappConfig={whatsappConfig}
            onOpenEmbed={() => setStepIndex(3)}
            onUpdated={(saved) => {
              setBot(saved);
              if (onSaved) onSaved(saved);
            }}
          />
          {canDelete ? (
            <section className={styles.dangerZone}>
              <h2>{t("sonic_prospect.danger_title")}</h2>
              <Button danger type="text" onClick={() => { setDeleteTyped(""); setDeleteOpen(true); }}>
                {t("sonic_prospect.delete_bot_btn")}
              </Button>
            </section>
          ) : null}
        </>
      )}
      <Modal
        open={deleteOpen}
        title={t("sonic_prospect.delete_bot_confirm_title")}
        okText={t("sonic_prospect.delete_bot_confirm_ok")}
        okButtonProps={{ danger: true, disabled: deleteTyped.trim() !== (bot.name || "").trim(), loading: deleting }}
        cancelText={t("sonic_prospect.confirm_cancel")}
        onCancel={() => setDeleteOpen(false)}
        onOk={handleDelete}
      >
        <p>{t("sonic_prospect.delete_type_name", { name: bot.name || "" })}</p>
        <Input value={deleteTyped} onChange={(e) => setDeleteTyped(e.target.value)} />
      </Modal>
    </div>
  );
};

export default BotWizard;
